import { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useRef } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { AutocompleteRef } from '@shared/components/autocomplete';
import { DraggableList } from '@shared/components/draggable-list';
import { Flex } from '@shared/components/flex';
import {
  CompanyLookup,
  CompanyLookupCard,
  CompanyLookupProps,
  isCompanyValueSame,
} from '@shared/components/lookups/company-lookup';
import { MixedCompany } from '@shared/models/company/company';

import { styles } from './CompanyMultiLookup.styles';

export interface CompanyMultiLookupProps
  extends Omit<CompanyLookupProps, 'classes' | 'onSelect'>,
    WithStyles<typeof styles> {
  draggable?: boolean;
  investment?: boolean;
  showLookup?: boolean;
  showRole?: boolean;
  value?: MixedCompany[];
  onChange?: (values: MixedCompany[]) => void;
  onRemove?: (value: MixedCompany) => void;
  onSelect?: CompanyLookupProps['onSelect'];
  onView?: (id: number) => void;
}

const CompanyMultiLookupComponent: ForwardRefRenderFunction<AutocompleteRef, CompanyMultiLookupProps> = (
  {
    classes,
    draggable,
    investment = false,
    showLookup = true,
    showRole,
    value,
    onChange,
    onRemove,
    onSelect,
    onView,
    ...lookupProps
  },
  ref
) => {
  const lookupRef = useRef<AutocompleteRef>(null);
  useImperativeHandle(ref, () => lookupRef.current as AutocompleteRef);

  const renderCard = (item: MixedCompany) => {
    return (
      <CompanyLookupCard
        classes={{ root: classes.lookupCard }}
        disabled={lookupProps.disabled}
        draggable={draggable}
        inputSize={lookupProps.inputProps?.size}
        investment={investment}
        item={item}
        key={'id' in item ? item.id : item.name}
        showRole={showRole}
        onRemove={
          onChange || onRemove
            ? (removedItem) => {
                if (onChange && value) {
                  onChange(value.filter((item) => !isCompanyValueSame(item, removedItem)));
                }
                if (onRemove) {
                  onRemove(removedItem);
                }
              }
            : undefined
        }
        onView={onView}
      />
    );
  };

  return (
    <Flex autoWidth={false} direction="column" classes={{ root: classes.root }}>
      {showLookup && (
        <CompanyLookup
          {...lookupProps}
          ref={lookupRef}
          onCreate={
            lookupProps.onCreate
              ? (data) => {
                  if (value?.some((item) => isCompanyValueSame(item, data))) {
                    return;
                  }
                  if (lookupProps.onCreate) {
                    lookupProps.onCreate(data);
                  }
                }
              : undefined
          }
          onSelect={(data) => {
            if (value?.some((item) => isCompanyValueSame(item, data))) {
              return;
            }
            if (onChange) {
              onChange(value ? [...value, data] : [data]);
            }
            if (onSelect) {
              onSelect(data);
            }
          }}
        />
      )}

      {!draggable && !!value?.length && (
        <Flex autoWidth={false} direction="column" classes={{ root: classes.list }}>
          {value.map((data) => renderCard(data))}
        </Flex>
      )}

      {!!draggable && !!value?.length && (
        <DraggableList
          classes={{ root: classes.draggableListRoot, list: classes.list, item: classes.draggableListItem }}
          disabled={lookupProps.disabled}
          items={value}
          onDragEnd={(items) => {
            if (onChange) {
              onChange(items as MixedCompany[]);
            }
          }}
          renderItem={(item) => renderCard(item as MixedCompany)}
        />
      )}
    </Flex>
  );
};

export const CompanyMultiLookup = withStyles(styles)(forwardRef(CompanyMultiLookupComponent));
